import { createServerFn } from "@tanstack/react-start";
import {
    recentlyViewedListingReferencesSchema,
    type RecordListingViewInput,
    recordListingViewInputSchema,
    type SavedListingInput,
    savedListingInputSchema,
    savedListingMutationResultSchema,
    savedListingReferencesSchema,
} from "./saved-listing.contract";
import { requireViewer, resolveViewerRef } from "@/lib/core/auth";
import { getViewer } from "@/lib/core/viewer-core.server";

export const saveListing = createServerFn({ method: "POST" })
    .inputValidator((data: SavedListingInput) =>
        savedListingInputSchema.parse(data),
    )
    .handler(async ({ data }) => {
        const [ref, viewer] = await Promise.all([
            resolveViewerRef(),
            getViewer(),
        ]);
        return savedListingMutationResultSchema.parse(
            await viewer
                .as(requireViewer(ref))
                .catalog.favorite(data.listingId),
        );
    });

export const unsaveListing = createServerFn({ method: "POST" })
    .inputValidator((data: SavedListingInput) =>
        savedListingInputSchema.parse(data),
    )
    .handler(async ({ data }) => {
        const [ref, viewer] = await Promise.all([
            resolveViewerRef(),
            getViewer(),
        ]);
        return savedListingMutationResultSchema.parse(
            await viewer
                .as(requireViewer(ref))
                .catalog.unfavorite(data.listingId),
        );
    });

export const getSavedListingIds = createServerFn({ method: "GET" }).handler(
    async () => {
        const [ref, viewer] = await Promise.all([
            resolveViewerRef(),
            getViewer(),
        ]);
        if (!ref) return savedListingReferencesSchema.parse([]);
        return savedListingReferencesSchema.parse(
            await viewer.as(ref).catalog.getFavoritedListingIds(),
        );
    },
);

export const recordListingViewed = createServerFn({ method: "POST" })
    .inputValidator((data: RecordListingViewInput) =>
        recordListingViewInputSchema.parse(data),
    )
    .handler(async ({ data }) => {
        const [ref, viewer] = await Promise.all([
            resolveViewerRef(),
            getViewer(),
        ]);
        if (!ref) return;
        await viewer
            .as(ref)
            .catalog.recordListingView(data.listingId, data.source);
    });

export const getRecentlyViewedListingIds = createServerFn({
    method: "GET",
}).handler(async () => {
    const [ref, viewer] = await Promise.all([resolveViewerRef(), getViewer()]);
    if (!ref) return recentlyViewedListingReferencesSchema.parse([]);
    return recentlyViewedListingReferencesSchema.parse(
        await viewer.as(ref).catalog.getRecentlyViewedListingIds(),
    );
});
